/**
 * clipPlayer — riproduzione cross-platform delle clip audio dell'utente
 * (Blob salvati in IndexedDB tramite audioClipStore).
 *
 * Strategia:
 *  - Su iOS/Android (Capacitor nativo): l'`<audio>` HTML5 dentro WKWebView
 *    NON gestisce sempre bene `URL.createObjectURL(blob)` (race con user gesture,
 *    blob URLs vengono spesso bloccate). Soluzione robusta:
 *      1. Convertiamo il Blob in base64 e lo scriviamo su Filesystem (cache dir)
 *      2. Passiamo il file URI a NativeAudio.preload({ isUrl: true })
 *      3. play() / stop()
 *  - Su web (preview/desktop): usiamo `<audio>` standard con `URL.createObjectURL`.
 *
 * Singleton: una sola clip in riproduzione alla volta. Le risorse (object URL,
 * file temporanei, preload native) vengono rilasciate prima di cambiare clip.
 */
import { Capacitor } from "@capacitor/core";
import { NativeAudio } from "@capacitor-community/native-audio";
import { Filesystem, Directory } from "@capacitor/filesystem";

const NATIVE_ASSET_ID = "smartdream_user_clip";
let currentObjectUrl = null;
let currentAudioEl = null;
let currentNativeFile = null; // path su Filesystem cache
let currentNativeAssetId = null;
let currentNativePreloaded = false;

function isNative() {
  const p = Capacitor.getPlatform();
  return p === "ios" || p === "android";
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result || "";
      // strip "data:audio/webm;base64,"
      const comma = String(result).indexOf(",");
      resolve(comma >= 0 ? String(result).slice(comma + 1) : String(result));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

async function cleanupNative() {
  if (currentNativePreloaded && currentNativeAssetId) {
    try {
      await NativeAudio.stop({ assetId: currentNativeAssetId });
    } catch {
      /* ignore */
    }
    try {
      await NativeAudio.unload({ assetId: currentNativeAssetId });
    } catch {
      /* ignore */
    }
    currentNativePreloaded = false;
    currentNativeAssetId = null;
  }
  if (currentNativeFile) {
    try {
      await Filesystem.deleteFile({
        path: currentNativeFile,
        directory: Directory.Cache,
      });
    } catch {
      /* ignore */
    }
    currentNativeFile = null;
  }
}

function cleanupWeb() {
  if (currentAudioEl) {
    try {
      currentAudioEl.pause();
      currentAudioEl.src = "";
    } catch {
      /* ignore */
    }
    currentAudioEl = null;
  }
  if (currentObjectUrl) {
    try {
      URL.revokeObjectURL(currentObjectUrl);
    } catch {
      /* ignore */
    }
    currentObjectUrl = null;
  }
}

/**
 * Riproduce un Blob audio. Ritorna un controller minimal { stop, isNative }.
 * Se il blob non è valido o la piattaforma rifiuta, ritorna { ok: false, reason }.
 */
export async function playClipBlob(blob, { onEnded } = {}) {
  if (!blob || !(blob instanceof Blob)) {
    return { ok: false, reason: "no_blob" };
  }

  // Stop tutto ciò che era in corso
  await stopClip();

  if (isNative()) {
    try {
      // 1. base64 → Filesystem cache
      const b64 = await blobToBase64(blob);
      // Determina l'estensione corretta in base al MIME type del blob.
      // iOS NativeAudio supporta: m4a/aac/mp4 (preferred), wav, mp3, caf.
      // NON supporta affidabilmente: webm/opus, ogg. Per webm proviamo
      // .m4a → iOS spesso riconosce comunque dal contenuto se header è AAC.
      const mime = (blob.type || "").toLowerCase();
      let ext = "m4a";
      if (mime.includes("wav")) ext = "wav";
      else if (mime.includes("mp3") || mime.includes("mpeg")) ext = "mp3";
      else if (mime.includes("aac")) ext = "aac";
      else if (mime.includes("caf")) ext = "caf";
      else if (mime.includes("mp4") || mime.includes("m4a")) ext = "m4a";
      else if (mime.includes("ogg") || mime.includes("webm")) {
        // iOS WKWebView NON supporta webm/opus nemmeno via NativeAudio.
        // Manteniamo l'estensione ma logghiamo per il chiamante.
        ext = "webm";
      }
      // assetId univoco per clip → evita collisioni se più clip preload
      // simultaneamente (anche se noi forziamo singleton).
      const assetId = `${NATIVE_ASSET_ID}_${Date.now()}`;
      const filename = `clip-${Date.now()}.${ext}`;
      const writeRes = await Filesystem.writeFile({
        path: filename,
        data: b64,
        directory: Directory.Cache,
      });
      currentNativeFile = filename;
      currentNativeAssetId = assetId;

      // 2. Preload via NativeAudio
      await NativeAudio.preload({
        assetId,
        assetPath: writeRes.uri,
        audioChannelNum: 1,
        isUrl: true,
        volume: 1.0,
      });
      currentNativePreloaded = true;

      // 3. Play
      await NativeAudio.play({ assetId });
      return {
        ok: true,
        platform: "native",
        format: ext,
        stop: () => stopClip(),
      };
    } catch (e) {
      await cleanupNative();
      return { ok: false, reason: "native_error", error: String(e) };
    }
  }

  // Web fallback
  try {
    const url = URL.createObjectURL(blob);
    currentObjectUrl = url;
    const audio = new Audio(url);
    audio.preload = "auto";
    currentAudioEl = audio;
    if (onEnded) audio.addEventListener("ended", onEnded);
    await audio.play();
    return { ok: true, platform: "web", stop: () => stopClip() };
  } catch (e) {
    cleanupWeb();
    return { ok: false, reason: "web_error", error: String(e) };
  }
}

export async function stopClip() {
  if (isNative()) {
    await cleanupNative();
  } else {
    cleanupWeb();
  }
}

/** Esposto per debug / unit test */
export const _state = () => ({
  hasNative: currentNativePreloaded,
  hasObjectUrl: !!currentObjectUrl,
  file: currentNativeFile,
});
